import { useEffect, useRef } from "react";
import { PushNotifications } from "@capacitor/push-notifications";
import { Capacitor } from "@capacitor/core";
import { toast } from "sonner";
import { notification } from "antd";
import { useAuthData } from "../includes/auth-provider.js";
import { NativeAudio } from "@capacitor-community/native-audio";
import { useHistory } from "react-router-dom";
import { Button, Space, Typography } from "antd";
import { ShoppingCartOutlined, BellFilled } from "@ant-design/icons";

export default function MobileNotificationWrapper() {
  const { user, organization_id } = useAuthData("dashboard");
  const audioCtxRef = useRef(null);
  const nativeAudioLoadedRef = useRef(false);
  const isPlayingRef = useRef(false);
  const { Text } = Typography;

  // Inside your MobileNotificationWrapper component:
  const history = useHistory();

  // 1. Preload the Alarm Sound
  useEffect(() => {
    const preloadNativeAudio = async () => {
      const platform = Capacitor.getPlatform();
      if (platform === "android" || platform === "ios") {
        try {
          await NativeAudio.preloadComplex({
            assetId: "alarm",
            filePath: "beep.mp3", // Ensure alarm.mp3 is in android/app/src/main/assets/
            volume: 1.0,
            audioChannelNum: 1,
            isUrl: false,
          });
          nativeAudioLoadedRef.current = true;
        } catch (err) {
          console.error("NativeAudio preload error:", err);
        }
      }
    };

    preloadNativeAudio();
  }, []);

  // 2. Logic to STOP the alarm
  const stopAlarm = async () => {
    const platform = Capacitor.getPlatform();
    isPlayingRef.current = false;

    if ((platform === "android" || platform === "ios") && nativeAudioLoadedRef.current) {
      try {
        await NativeAudio.stop({ assetId: "alarm" });
      } catch (e) {
        console.error("Error stopping native audio", e);
      }
    } else if (audioCtxRef.current) {
      await audioCtxRef.current.suspend();
    }
  };

  // 3. Show Ant Design Notification with manual close trigger
  const showOrderNotification = (notif) => {
    // Extract order number from the data payload sent by FCM
    const orderNumber = notif.data?.order_number || "New";
    const notificationKey = `order-${orderNumber}-${Date.now()}`;

    const handleAction = () => {
      stopAlarm(); // Stop the sound
      notification.destroy(notificationKey); // Remove the notification popup
      history.push("/ecommerce-orders");
    };

    notification.open({
      key: notificationKey,
      message: (
        <Space>
          <BellFilled style={{ color: "#1890ff" }} />
          <Text strong>New Order Received</Text>
        </Space>
      ),
      description: (
        <div style={{ marginTop: "8px" }}>
          <p>Order <Text code>#{orderNumber}</Text> has been successfully placed.</p>
          <Button
            type="primary"
            size="small"
            block
            onClick={handleAction}
            style={{ marginTop: "10px" }}
          >
            View Order Details
          </Button>
        </div>
      ),
      placement: "topRight",
      duration: 0, // Keeps it visible until the user acts
      onClose: stopAlarm, // Silences if the 'X' is clicked
    });
  };

  // 4. Play Persistent Alarm
  const playAlarm = async () => {
    if (isPlayingRef.current) return;
    isPlayingRef.current = true;

    try {
      const platform = Capacitor.getPlatform();

      // MOBILE LOOP
      if ((platform === "android" || platform === "ios") && nativeAudioLoadedRef.current) {
        await NativeAudio.loop({ assetId: "alarm" });
        return;
      }

      // WEB FALLBACK LOOP
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      await ctx.resume();

      const runWebAlarm = () => {
        if (!isPlayingRef.current) return;
        const osc = ctx.createOscillator();
        const gainNode = ctx.createGain();
        osc.type = "square";
        osc.frequency.value = 880;
        gainNode.gain.value = 0.1;
        osc.connect(gainNode);
        gainNode.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.5);
        setTimeout(runWebAlarm, 1000);
      };
      runWebAlarm();

    } catch (err) {
      console.error("Alarm error:", err);
    }
  };

  // 5. Initialize Push Notifications & FCM Token API
  useEffect(() => {
    if (!user) return;

    if (Capacitor.getPlatform() === "web") return;
    // if (Capacitor.getPlatform() === "web") {
    //   fetch(`${process.env.REACT_APP_SERVER_URL}/save-fcm-token`, {
    //     method: "POST",
    //     headers: { "Content-Type": "application/json" },
    //     body: JSON.stringify({
    //       user_id: "test-user@example.com",
    //       organization_id: "test-org-123",
    //       fcm_token: "fake-token-12345-web-test"
    //     }),
    //   }).then(res => res.text()).then(console.log);
    // }

    const initPush = async () => {
      try {
        const perm = await PushNotifications.requestPermissions();
        if (perm.receive !== "granted") return;

        await PushNotifications.register();

        // SAVE FCM TOKEN API CALL
        PushNotifications.addListener("registration", async (token) => {
          console.log("FCM TOKEN RECEIVED:", token.value);
          await fetch(`${process.env.REACT_APP_SERVER_URL}/save-fcm-token`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_id: user.email,
              organization_id,
              fcm_token: token.value
            }),
          });
        });

        // HANDLE RECEIVED NOTIFICATION
        PushNotifications.addListener("pushNotificationReceived", async (notif) => {
          // 1. Show the professional UI
          showOrderNotification(notif);

          // 2. Trigger the persistent sound/vibration
          await playAlarm();
          if (navigator.vibrate) {
            navigator.vibrate([500, 200, 500]);
          }
        });

        PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
          stopAlarm(); // Stop alarm if they tap the system tray notification
          console.log("Action performed:", action.notification.data);
        });

      } catch (err) {
        console.error("initPush err", err);
      }
    };

    initPush();

    return () => {
      PushNotifications.removeAllListeners();
    };
  }, [user, organization_id]);

  return null;
}